import ComponentManager from "../../../componentFramework";
import PrimeFacesUtil from "../PrimeFacesUtil";
import EventListenerManager from "../../EventListenerManager";
import eventListenerOption from "../../../scripts/eventListenerOption";


/**
 * Enhances prime:selectOneMenu
 * Documentation for PrimeFaces component: https://primefaces.github.io/primefaces/8_0/#/components/selectonemenu
 *
 * Enhancements:
 *   * Dispatches a "change" event when a new item is selected
 *	 * Show the options on focus of the input field (this is when editable="true")
 *	 * Adds some placeholder text to the input used for filtering so the user knows what it does
 *	 * Closes the dropdown panel when there is a scroll event (otherwise it just sits "fixed" and looks bad)
 *	 * Clicking the label activates the field's focus / dropdown
 *
 *   TODO (Michael) - Additional enhancements which would be good:
 *   	1) Make arrow keys, enter and escape work. PrimeFaces does have these keys setup if the dropdown has focus. How can we piggyback off that?
 *   	2) If I click "tab" I want it to focus the filter field next which is out of order. How can I do that?
 *   	3) The field can not be tabbed to if editable="false" (the display is strangely a label.
 */
@ComponentManager.register()
export default class PrimeFacesSelectOneMenu {
	public static componentStyleClass = "ui-selectonemenu";
	private readonly eventListenerManager = new EventListenerManager();
	private originalSelectItem: any;
	private primeFacesWidget: any = null;

	constructor(private readonly rootElement: HTMLElement) {
	}

	@ComponentManager.on("beforeFirstConstruction")
	private static fixPrimeFacesDynamicLoadingOfTables() {
		const PrimeFaces = (window as any).PrimeFaces;
		const originalHandle = PrimeFaces.ajax.Request.handle;
		PrimeFaces.ajax.Request.handle = (requestSettings: any, paramsMaybe: any) => {
			const localWidget = PrimeFaces.getWidgetById(requestSettings.source);
			if (localWidget.type === "PrimeFacesSelectOneMenu--enhanced") {
				if ((requestSettings.params as {name: string, value: any}[]).findIndex(v => v.name === requestSettings.source + "_dynamicload" && v.value) !== -1) {
					requestSettings.onsuccess = function (e: any, c: any, d: any) {
						PrimeFaces.ajax.Response.handle(e, c, d, {
							widget: localWidget,
							handle: function (i: any) {
								const h = $($.parseHTML(i));
								const g = h.filter("ul,table");
								localWidget.itemsWrapper.empty();
								localWidget.itemsWrapper.append(g);
								const f = h.filter("select");
								localWidget.input.replaceWith(f)
							}
						});
						return true
					};
				}
			}
			return originalHandle(requestSettings, paramsMaybe);
		};
	}

	@ComponentManager.on("ready")
	private componentReady() {
		PrimeFacesUtil.addWidgetListener("init", this.getPrimeFacesWidgetId(), this.onWidgetReady, true);
	}

	private onWidgetReady = (widget: any) => {
		if (!this.rootElement.isConnected) {
			return;
		}
		const potential = this.rootElement.getAttribute("data-initial-label");
		if (potential !== null && potential.length > 0) {
			widget.setLabel(potential);
		}

		widget.type = "PrimeFacesSelectOneMenu--enhanced";

		this.primeFacesWidget = widget;

		const _this = this;

		// Disable the effects. I want snappy; not the default effects.
		widget.cfg.effect = "none";

		// Dispatches a "change" event when a new item is selected
		this.originalSelectItem = widget.selectItem;
		widget.selectItem = function(a: any, b: any) {
			_this.originalSelectItem.call(this, a, b);
			_this.rootElement.querySelector("input")?.dispatchEvent(new Event("change", {bubbles: true, cancelable: false}));
		}

		//
		const inputField = this.getEditableInput();
		if (inputField !== null) {
			this.eventListenerManager.manage(inputField).addEventListener("focus", () => {
				// Show the dropdown. That will cause the focus to the filter field, so we override PrimeFace's method, but revert it back after it is called once.
				const widget = this.getPrimeFacesWidget();
				if (widget !== null) {
					const originalFocusFilter = widget.focusFilter;
					widget.focusFilter = () => {
						// Do nothing. Except set the method back to the original
						widget.focusFilter = originalFocusFilter;
					};
					widget.show();
				}
			});
		}

		// Set placeholder text on the filter input
		let dropdownPanel = this.getDropdownPanelElement();
		if (dropdownPanel !== null) {
			const filterInput = dropdownPanel.querySelector("." + PrimeFacesSelectOneMenu.componentStyleClass + "-filter") as HTMLInputElement;
			if (filterInput !== null) {
				filterInput.placeholder = "Type here to filter list";
			}
		}

		// Connect the label with the component
		const label = this.rootElement.ownerDocument.querySelector(`label[for="${this.rootElement?.id}"]`);
		if (label !== null) {
			this.eventListenerManager.manage(label).addEventListener("click", () => {
				const editableInput = this.getEditableInput();
				if (editableInput !== null) {
					editableInput.focus();
				} else {
					this.getPrimeFacesWidget()?.show();
				}
			});
		}

		// Hide on scroll
		this.eventListenerManager.manage(window).addEventListener("scroll", this.onWinScroll, eventListenerOption.passiveCapture);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
		PrimeFacesUtil.removeWidgetListener("init", this.getPrimeFacesWidgetId(), this.onWidgetReady);
		if (this.originalSelectItem !== undefined) {
			this.getPrimeFacesWidget().selectItem = this.originalSelectItem;
			this.originalSelectItem = undefined;
		}
	}


	private onWinScroll = (ev: Event) => {
		if (this.rootElement.ownerDocument.defaultView !== null && ev.target instanceof this.rootElement.ownerDocument.defaultView.Node) {
			// Was a scroll event form this component.
			if (this.rootElement.contains(ev.target)) {
				return;
			} else {
				const dropdownPanel = this.getDropdownPanelElement();
				if (dropdownPanel !== null && dropdownPanel.contains(ev.target)) {
					return;
				}
			}
		}

		this.getPrimeFacesWidget()?.hide();
	}

	private getEditableInput() {
		return this.rootElement.querySelector<HTMLInputElement>("input." + PrimeFacesSelectOneMenu.componentStyleClass + "-label");
	}

	private getDropdownPanelElement(): HTMLElement|null {
		let dropdownPanel = this.getPrimeFacesWidget()?.panel;
		if (dropdownPanel !== undefined && dropdownPanel !== null) {
			if (dropdownPanel.jquery !== undefined) {
				// It is a jquery object
				dropdownPanel = dropdownPanel.get()[0];
			}
			return dropdownPanel;
		}
		return null;
	}

	private getPrimeFacesWidget() {
		return this.primeFacesWidget;
	}

	private getPrimeFacesWidgetId() {
		return this.rootElement.id;
	}
}
